import {DateTime} from '../services/dateTime';

import React, { useState, useEffect } from 'react';
import { MockAPI } from '../services/api';
import { ScoutingClient } from '../services/scoutingClient';
import { CorrespondenceMessage, CorrespondenceChannel, CorrespondenceDirection } from '../types';
import { Button } from './ui';
import { MessageSquare, Phone, Mail, FileText, Send, Trash2 } from 'lucide-react';

interface NotesFeedProps {
    entityType: string;
    entityId: string;
    targets?: { entityType: string; entityId: string }[]; // Optional aggregation
    title?: string;
    className?: string;
}

const NotesFeed: React.FC<NotesFeedProps> = ({ entityType, entityId, targets, title = "Notes & Correspondence", className = "" }) => {
    const [messages, setMessages] = useState<CorrespondenceMessage[]>([]);
    const [isExpanded, setIsExpanded] = useState(false);
    
    // New Note State
    const [newNoteText, setNewNoteText] = useState('');
    const [newChannel, setNewChannel] = useState<CorrespondenceChannel>(CorrespondenceChannel.InternalNote);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const refreshMessages = async () => {
        if (entityType === 'scouting_photo') {
            const msgs = await ScoutingClient.listComments(entityId);
            setMessages(msgs);
            return;
        }

        if (targets && targets.length > 0) {
            const msgs = await MockAPI.getMessagesForEntities(targets);
            setMessages(msgs);
        } else {
            const msgs = await MockAPI.getMessagesForEntity(entityType, entityId);
            setMessages(msgs);
        }
    };

    useEffect(() => {
        refreshMessages();
    }, [entityType, entityId, targets]);

    const handleAddNote = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newNoteText.trim()) return;

        setIsSubmitting(true);
        
        const newMessage: CorrespondenceMessage = {
            message_id: '',
            direction: CorrespondenceDirection.Outbound, // Default to outbound/internal
            channel: newChannel,
            body_text: newNoteText,
            message_datetime: new Date().toISOString(),
            has_attachment: false,
            sender: 'Me',
            server_version: 0
        };

        try {
            // New notes are always attached to the primary entity context, even in aggregated view
            if (entityType === 'scouting_photo') {
                await ScoutingClient.addComment(entityId, { body_text: newNoteText, channel: newChannel });
            } else {
                await MockAPI.saveNoteForEntity(newMessage, entityType, entityId);
            }
            setNewNoteText('');
            setNewChannel(CorrespondenceChannel.InternalNote); // Reset to default
            refreshMessages();
        } catch (err) {
            console.error("Failed to save note", err);
            alert("Failed to save note.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async (id: string) => {
        if (confirm("Delete this note?")) {
            if (entityType === 'scouting_photo') {
                await MockAPI.deleteMessage(id);
            } else {
                await MockAPI.deleteMessage(id);
            }
            refreshMessages();
        }
    };

    const getIcon = (channel: CorrespondenceChannel) => {
        switch (channel) {
            case CorrespondenceChannel.Email: return <Mail className="h-4 w-4 text-blue-500" />;
            case CorrespondenceChannel.PhoneLog: return <Phone className="h-4 w-4 text-green-500" />;
            case CorrespondenceChannel.PostalLetter: return <FileText className="h-4 w-4 text-amber-600" />;
            default: return <MessageSquare className="h-4 w-4 text-gray-500" />;
        }
    };

    return (
        <div className={`bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden flex flex-col ${className}`}>
            <div className="p-4 border-b border-gray-100 bg-gray-50 flex justify-between items-center">
                <h3 className="font-semibold text-gray-900 flex items-center">
                    <MessageSquare className="h-4 w-4 mr-2" /> {title}
                </h3>
                <span className="bg-gray-200 text-gray-600 text-xs font-medium px-2 py-0.5 rounded-full">
                    {messages.length}
                </span>
            </div>

            <div className="flex-1 overflow-y-auto max-h-[400px] p-4 bg-gray-50/50">
                {messages.length === 0 ? (
                    <div className="text-center py-8 text-gray-400 text-sm italic">
                        No notes recorded yet.
                    </div>
                ) : (
                    <ul className="space-y-4">
                        {messages.map((msg) => (
                            <li key={msg.message_id} className="relative flex gap-x-4 group">
                                <div className={`absolute -bottom-6 left-0 top-0 flex w-6 justify-center ${messages.indexOf(msg) === messages.length - 1 ? 'h-6' : ''}`}>
                                    <div className="w-px bg-gray-200"></div>
                                </div>
                                <div className="relative flex h-6 w-6 flex-none items-center justify-center bg-white rounded-full border border-gray-200 shadow-sm mt-1">
                                    {getIcon(msg.channel)}
                                </div>
                                <div className="flex-auto rounded-md p-3 ring-1 ring-inset ring-gray-200 bg-white shadow-sm">
                                    <div className="flex justify-between gap-x-4">
                                        <div className="py-0.5 text-xs leading-5 text-gray-500">
                                            <span className="font-medium text-gray-900 mr-2">
                                                {msg.channel === CorrespondenceChannel.InternalNote ? 'Note' : msg.channel}
                                            </span>
                                            <span className="text-gray-400">
                                                {<DateTime value={msg.message_datetime}/>}
                                            </span>
                                        </div>
                                        <button 
                                            onClick={() => handleDelete(msg.message_id)} 
                                            className="text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                                            title="Delete Note"
                                        >
                                            <Trash2 className="h-3 w-3" />
                                        </button>
                                    </div>
                                    <p className="text-sm leading-6 text-gray-700 mt-1 whitespace-pre-wrap">
                                        {msg.body_text}
                                    </p>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* Input Area */}
            <div className="p-4 bg-white border-t border-gray-100">
                <form onSubmit={handleAddNote} className="space-y-3">
                    <textarea
                        rows={isExpanded ? 3 : 2}
                        onFocus={() => setIsExpanded(true)}
                        placeholder="Log a note, phone call, or email..."
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-usda-500 focus:ring-usda-500 sm:text-sm p-2 border resize-none"
                        value={newNoteText}
                        onChange={(e) => setNewNoteText(e.target.value)}
                    />
                    
                    {/* Controls Row - Only show when expanded or typing */}
                    {(isExpanded || newNoteText) && (
                        <div className="flex items-center justify-between animate-in fade-in slide-in-from-top-1 duration-200">
                            <div className="flex items-center space-x-2">
                                <select
                                    value={newChannel}
                                    onChange={(e) => setNewChannel(e.target.value as CorrespondenceChannel)}
                                    className="block w-32 rounded-md border-gray-300 py-1.5 pl-3 pr-8 text-xs focus:border-usda-500 focus:outline-none focus:ring-usda-500 sm:text-xs border"
                                >
                                    <option value={CorrespondenceChannel.InternalNote}>Note</option>
                                    <option value={CorrespondenceChannel.PhoneLog}>Phone Call</option>
                                    <option value={CorrespondenceChannel.Email}>Email</option>
                                    <option value={CorrespondenceChannel.PostalLetter}>Letter</option>
                                </select>
                            </div>
                            <Button size="sm" type="submit" disabled={!newNoteText.trim() || isSubmitting}>
                                <Send className="h-3 w-3 mr-1" /> {isSubmitting ? 'Saving...' : 'Add Log'}
                            </Button>
                        </div>
                    )}
                </form>
            </div>
        </div>
    );
};

export default NotesFeed;
